import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import {
  type EdgeId,
  type PathLookupDeviceDecisions,
  type PathLookupGraphPreset,
  safeAbsurd,
} from '@ipfabric/utils';
import classNames from 'classnames';
import type {
  DeviceViewNodeEntity,
  PathInspectorNodePackets,
  PathInspectorPacket,
} from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Select, { type PropsValue } from 'react-select';
import type { PacketDetailMouseEvent } from '../PacketDetail/PacketDetail';
import {
  PathInspectorPacketDetail,
  type PathInspectorPacketSelectEvent,
} from './PathInspectorPacketDetail';
import { hasPathInspectorPackets } from './helpers/hasPathInspectorPackets';

import { reactSelectStyles } from 'containers/App/styles/reactSelectStyles';
import { reactSelectTheme } from 'containers/App/styles/reactSelectTheme';
import type { AppTranslations } from 'translations/translations';
import { getPacketSeverityInfo } from './helpers/getPacketSeverityInfo';
import { getPathInspectorPacketHighestSeverity } from './helpers/getPathInspectorPacketHighestSeverity';

export interface SelectPacketMouseEvent {
  edgeId: EdgeId;
}

interface Props {
  decision?: PathLookupDeviceDecisions;
  deviceViewEntity: DeviceViewNodeEntity;
  hoveredPathInspectorEdge?: EdgeId | null;
  selectType: 'first' | 'second';
  onSelectPacketHover?: (event: SelectPacketMouseEvent) => void;
  onSelectPacketLeave?: (event: SelectPacketMouseEvent) => void;
  onPathInspectorPacketSelect?: (event: PathInspectorPacketSelectEvent) => void;
  pathInspectorNodePackets?: PathInspectorNodePackets;
  pathLookupPreset: PathLookupGraphPreset;
  sourcePacketId?: EdgeId;
}

type PacketSort =
  | 'acceptedAscending'
  | 'acceptedDescending'
  | 'droppedAscending'
  | 'droppedDescending'
  | 'severityAscending'
  | 'severityDescending';

interface OptionType {
  value: PacketSort;
  label: string;
}

const getPacketType = (
  pathInspectorNodePackets: PathInspectorNodePackets,
  pathInspectorPacketId: EdgeId,
) =>
  pathInspectorNodePackets.generatedPackets.some(
    (p) => p.id === pathInspectorPacketId,
  )
    ? 'generatedPackets'
    : 'edge';

const getSelectPacketTitle = (
  msg: AppTranslations,
  selectType: 'first' | 'second',
  pathInspectorNodePackets: PathInspectorNodePackets,
): string => {
  if (selectType === 'second') {
    return msg.deviceView.devicePathLookup.selectPacket.second.title;
  }
  return msg.deviceView.devicePathLookup.selectPacket.first[
    pathInspectorNodePackets.generatedPackets.length === 0
      ? 'incomingTitle'
      : 'title'
  ];
};
const BarePathInspectorSelectPacketFromList: FC<Props> = (props) => {
  const msg = useLocale();
  const [packetsAnimation, setPacketsAnimation] = useState<
    Map<EdgeId, boolean>
  >(new Map());
  const [sortIncomingEdges, setSortIncomingEdges] =
    useState<PacketSort>('droppedAscending');
  const [showAcceptedPackets, setShowAcceptedPackets] = useState<boolean>(
    props.selectType === 'second',
  );
  const [showDroppedPackets, setShowDroppedPackets] = useState<boolean>(
    props.selectType === 'second',
  );
  const [showForwardedPackets, setShowForwardedPackets] =
    useState<boolean>(true);

  const sortOptions: OptionType[] = [
    {
      value: 'acceptedAscending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsAcceptedAscending,
    },
    {
      value: 'acceptedDescending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsAcceptedDescending,
    },
    {
      value: 'droppedAscending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsDroppedAscending,
    },
    {
      value: 'droppedDescending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsDroppedDescending,
    },
    {
      value: 'severityAscending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsSeverityAscending,
    },
    {
      value: 'severityDescending',
      label:
        msg.deviceView.devicePathLookup.selectPacket
          .sortByPacketsSeverityDescending,
    },
  ];

  const handlePacketDetailHover = (event: PacketDetailMouseEvent) => {
    setPacketsAnimation(new Map(packetsAnimation.set(event.detailId, true)));
    props.onSelectPacketHover?.({ edgeId: event.detailId });
  };

  const handlePacketDetailLeave = (event: PacketDetailMouseEvent) => {
    setPacketsAnimation(new Map(packetsAnimation.set(event.detailId, false)));
    props.onSelectPacketLeave?.({ edgeId: event.detailId });
  };

  const handleSortChange = (option: PropsValue<OptionType>) => {
    // Retyped due to bug in react-select types
    // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/32553
    setSortIncomingEdges((option as OptionType).value);
  };

  const handleShowAcceptedPacketsChange = () => {
    setShowAcceptedPackets(!showAcceptedPackets);
  };

  const handleShowDroppedPacketsChange = () => {
    setShowDroppedPackets(!showDroppedPackets);
  };

  const handleShowForwardedPacketsChange = () => {
    setShowForwardedPackets(!showForwardedPackets);
  };

  if (props.pathInspectorNodePackets === undefined) {
    return null;
  }

  return (
    <div
      className={classNames(
        'path-inspector-packet-select__list',
        'flex flex-1 flex-col',
      )}
    >
      <div className='pathlookup-select-packets flex flex-1 flex-col'>
        <div className='pathlookup-select-packets__node-title flex items-center whitespace-nowrap p-5'>
          <span className='pathlookup-select-packets__step'>
            {
              msg.deviceView.devicePathLookup.selectPacket[props.selectType]
                .step
            }
          </span>
          <div className='ml-lgu-3 mr-lgu-2 shrink-0 font-bold'>
            <FormattedMessage
              id='deviceView.selectedPackket.title'
              defaultMessage={getSelectPacketTitle(
                msg,
                props.selectType,
                props.pathInspectorNodePackets,
              )}
              values={{ nodeTitle: props.deviceViewEntity.title }}
            />
          </div>
          <div className='mr-10 flex-1'>
            <Tooltip
              content={
                msg.deviceView.devicePathLookup.selectPacket[props.selectType]
                  .info
              }
            >
              <span className='pathlookup-select-packets__tooltip'>?</span>
            </Tooltip>
          </div>
          {props.selectType === 'first' && (
            <Select
              className='pathlookup-select-packets__sort'
              classNamePrefix='react-select'
              name='path-inspector-sort-by-packet'
              onChange={handleSortChange}
              styles={reactSelectStyles}
              theme={reactSelectTheme}
              options={sortOptions}
              value={
                sortOptions.find(
                  (option) => option.value === sortIncomingEdges,
                ) ?? null
              }
            />
          )}
          {props.selectType === 'second' && (
            <div className='shrink-0'>
              <label className='mb-0 align-middle'>
                <input
                  checked={showAcceptedPackets}
                  className='pathlookup-select-packets__show-packet'
                  onChange={handleShowAcceptedPacketsChange}
                  type='checkbox'
                />
                <span className='mx-lgu-2 align-middle'>
                  {
                    msg.deviceView.devicePathLookup.selectPacket
                      .showAcceptedPackets
                  }
                </span>
                <span
                  className={classNames(
                    'pathlookup-select-packets__show-packet-count',
                    'pathlookup-select-packets__show-packet-count--accepted',
                  )}
                >
                  {props.pathInspectorNodePackets.acceptedPackets.length}
                </span>
              </label>
              <label className='mx-lgu-12 mb-0 align-middle'>
                <input
                  checked={showDroppedPackets}
                  className='pathlookup-select-packets__show-packet'
                  onChange={handleShowDroppedPacketsChange}
                  type='checkbox'
                />
                <span className='mx-lgu-2 align-middle'>
                  {
                    msg.deviceView.devicePathLookup.selectPacket
                      .showDroppedPackets
                  }
                </span>
                <span
                  className={classNames(
                    'pathlookup-select-packets__show-packet-count',
                    'pathlookup-select-packets__show-packet-count--dropped',
                  )}
                >
                  {props.pathInspectorNodePackets.droppedPackets.length}
                </span>
              </label>
              <label className='mb-0 align-middle'>
                <input
                  checked={showForwardedPackets}
                  className='pathlookup-select-packets__show-packet'
                  onChange={handleShowForwardedPacketsChange}
                  type='checkbox'
                />
                <span className='mx-lgu-2 align-middle'>
                  {
                    msg.deviceView.devicePathLookup.selectPacket
                      .showForwardedPackets
                  }
                </span>
                <span
                  className={classNames(
                    'pathlookup-select-packets__show-packet-count',
                    'pathlookup-select-packets__show-packet-count--forwarded',
                  )}
                >
                  {props.pathInspectorNodePackets.edgePackets.length}
                </span>
              </label>
            </div>
          )}
        </div>
        <div className='flex flex-1 flex-col'>
          {hasPathInspectorPackets(props.pathInspectorNodePackets) ? (
            <div className='flex flex-1'>
              {showAcceptedPackets &&
                props.pathInspectorNodePackets.acceptedPackets.map(
                  (
                    pathInspectorPacket: PathInspectorPacket,
                    packetIndex: number,
                  ) => (
                    <PathInspectorPacketDetail
                      animate={
                        packetsAnimation.get(pathInspectorPacket.id) ?? false
                      }
                      deviceViewEntityNodeSn={props.deviceViewEntity.sn}
                      key={pathInspectorPacket.id}
                      onPacketDetailHover={handlePacketDetailHover}
                      onPacketDetailLeave={handlePacketDetailLeave}
                      onPathInspectorPacketSelect={
                        props.onPathInspectorPacketSelect
                      }
                      packet={pathInspectorPacket.packet}
                      packetId={pathInspectorPacket.id}
                      packetIndex={packetIndex}
                      pathLookupPreset={props.pathLookupPreset}
                      selectType={props.selectType}
                      severityInfo={getPacketSeverityInfo(
                        props.sourcePacketId,
                        pathInspectorPacket.id,
                        props.decision,
                      )}
                      sourceLabel={null}
                      targetLabel={null}
                      type='acceptedPackets'
                    />
                  ),
                )}
              {showDroppedPackets &&
                props.pathInspectorNodePackets.droppedPackets.map(
                  (
                    pathInspectorPacket: PathInspectorPacket,
                    packetIndex: number,
                  ) => (
                    <PathInspectorPacketDetail
                      animate={
                        packetsAnimation.get(pathInspectorPacket.id) ?? false
                      }
                      deviceViewEntityNodeSn={props.deviceViewEntity.sn}
                      key={pathInspectorPacket.id}
                      onPacketDetailHover={handlePacketDetailHover}
                      onPacketDetailLeave={handlePacketDetailLeave}
                      onPathInspectorPacketSelect={
                        props.onPathInspectorPacketSelect
                      }
                      packet={pathInspectorPacket.packet}
                      packetId={pathInspectorPacket.id}
                      packetIndex={packetIndex}
                      pathLookupPreset={props.pathLookupPreset}
                      selectType={props.selectType}
                      severityInfo={getPacketSeverityInfo(
                        props.sourcePacketId,
                        pathInspectorPacket.id,
                        props.decision,
                      )}
                      sourceLabel={null}
                      targetLabel={null}
                      type='droppedPackets'
                    />
                  ),
                )}
              {showForwardedPackets &&
                [
                  ...props.pathInspectorNodePackets.edgePackets,
                  ...(props.selectType === 'first'
                    ? props.pathInspectorNodePackets.generatedPackets
                    : []),
                ]
                  .sort((a: PathInspectorPacket, b: PathInspectorPacket) => {
                    switch (sortIncomingEdges) {
                      case 'acceptedAscending':
                        return (
                          (a.trafficScore?.accepted ?? 0) -
                          (b.trafficScore?.accepted ?? 0)
                        );
                      case 'acceptedDescending':
                        return (
                          (a.trafficScore?.accepted ?? 0) -
                          (b.trafficScore?.accepted ?? 0)
                        );
                      case 'droppedAscending':
                        return (
                          (a.trafficScore?.dropped ?? 0) -
                          (b.trafficScore?.dropped ?? 0)
                        );
                      case 'droppedDescending':
                        return (
                          (b.trafficScore?.dropped ?? 0) -
                          (a.trafficScore?.dropped ?? 0)
                        );
                      case 'severityAscending':
                        return (
                          getPathInspectorPacketHighestSeverity(a) -
                          getPathInspectorPacketHighestSeverity(b)
                        );
                      case 'severityDescending':
                        return (
                          getPathInspectorPacketHighestSeverity(b) -
                          getPathInspectorPacketHighestSeverity(a)
                        );
                      default:
                        safeAbsurd(sortIncomingEdges, 'unknown sort option');
                        return 0;
                    }
                  })
                  .map(
                    (
                      pathInspectorPacket: PathInspectorPacket,
                      packetIndex: number,
                    ) => (
                      <PathInspectorPacketDetail
                        animate={
                          props.hoveredPathInspectorEdge ===
                            pathInspectorPacket.id ||
                          !!packetsAnimation.get(pathInspectorPacket.id)
                        }
                        deviceViewEntityNodeSn={props.deviceViewEntity.sn}
                        edgeDirection={pathInspectorPacket.edgeDirection}
                        key={pathInspectorPacket.id}
                        labels={pathInspectorPacket.labels}
                        onPacketDetailHover={handlePacketDetailHover}
                        onPacketDetailLeave={handlePacketDetailLeave}
                        onPathInspectorPacketSelect={
                          props.onPathInspectorPacketSelect
                        }
                        packet={pathInspectorPacket.packet}
                        packetId={pathInspectorPacket.id}
                        packetIndex={packetIndex}
                        pathLookupPreset={props.pathLookupPreset}
                        selectType={props.selectType}
                        severityInfo={
                          props.selectType === 'first'
                            ? pathInspectorPacket.severityInfo
                            : getPacketSeverityInfo(
                                props.sourcePacketId,
                                pathInspectorPacket.id,
                                props.decision,
                              )
                        }
                        sourceLabel={pathInspectorPacket.sourceLabel ?? null}
                        targetLabel={pathInspectorPacket.targetLabel ?? null}
                        trafficScore={pathInspectorPacket.trafficScore}
                        type={getPacketType(
                          props.pathInspectorNodePackets!,
                          pathInspectorPacket.id,
                        )}
                      />
                    ),
                  )}
            </div>
          ) : (
            <div className='pathlookup-select-packets__no-terminated-packets whitespace-pre p-5'>
              {msg.deviceView.devicePathLookup.selectPacket.noItemsToShow}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const PathInspectorSelectPacketFromList = memo(
  BarePathInspectorSelectPacketFromList,
);
